import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import {
  api,
  ApiError,
  type PlayerProfileAchievement,
  type PlayerProfileMatch,
  type PlayerProfilePayload,
  type PlayerProfileTournamentRow,
} from "../lib/api";
import { routes } from "../routes";
import RankMovementBadge from "../components/RankMovementBadge";
import PlayerFamilyProfileSwitcher from "../components/PlayerFamilyProfileSwitcher";
import { formatDate } from "../lib/dateFormat";
import { makeLatestOnly } from "../lib/latestOnly";
import { matchScoreFromWinner } from "../lib/scoreFromWinner";
import { participantStatusLabel, tournamentTypeLabel } from "../lib/playerLabels";
import { usePlayerProfileScope } from "../lib/usePlayerProfileScope";

function formatPercent(value: number | null) {
  if (value === null) return "—";
  return `${Math.round(value * 100)}%`;
}

function initials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return (parts[0]?.[0] ?? "J") + (parts[1]?.[0] ?? "");
}

function recordLabel(stats: PlayerProfilePayload["stats"]) {
  return `${stats.wins}–${stats.losses}${stats.draws ? `–${stats.draws}` : ""}`;
}

function featuredTournament(rows: PlayerProfileTournamentRow[]) {
  return [...rows].sort((a, b) => {
    const da = a.participant.rank_delta ?? -999;
    const db = b.participant.rank_delta ?? -999;
    return db - da;
  })[0] ?? null;
}

function StatTile({
  label,
  value,
  hint,
  accent,
}: {
  label: string;
  value: string | number;
  hint?: string;
  accent?: boolean;
}) {
  return (
    <div className={accent ? "metric-card metric-card--accent" : "metric-card"}>
      <span className="metric-card__label">{label}</span>
      <span className="metric-card__value">{value}</span>
      {hint && <span className="metric-card__hint">{hint}</span>}
    </div>
  );
}

function AthleteHero({
  data,
  isPublicProfile,
  avatarBusy = false,
  onUploadAvatar,
  onRemoveAvatar,
}: {
  data: PlayerProfilePayload;
  isPublicProfile: boolean;
  avatarBusy?: boolean;
  onUploadAvatar?: (file: File) => void;
  onRemoveAvatar?: () => void;
}) {
  const profile = data.profile;
  const canEditAvatar = !isPublicProfile && Boolean(onUploadAvatar);
  const featured = featuredTournament(data.active_tournaments);
  const initialRank = featured?.participant.initial_rank ?? featured?.participant.tournament_rank ?? null;
  const currentRank = featured?.participant.current_rank ?? null;
  // Rank oficial (FMT) y WTN del jugador (pedido de Eduardo 2026-07-15).
  const fmtRanking = profile.official_rankings?.[0] ?? null;
  const subtitle = [
    profile.primary_category?.name,
    profile.city,
    featured?.tournament?.name,
  ].filter(Boolean).join(" · ") || "Jugador Torneos123";

  return (
    <section className="player-athlete-hero">
      <div className="player-athlete-hero__identity">
        <div className="player-athlete-hero__avatar-block">
          <div className="player-athlete-hero__avatar" aria-hidden={profile.avatar_url ? undefined : true}>
            {profile.avatar_url ? (
              <img src={profile.avatar_url} alt={`Foto de ${profile.display_name}`} />
            ) : (
              initials(profile.display_name)
            )}
          </div>
          {canEditAvatar && (
            <div className="player-athlete-hero__avatar-actions">
              <label className={`btn btn--ghost btn--compact${avatarBusy ? " is-disabled" : ""}`}>
                {avatarBusy ? "Subiendo…" : profile.avatar_url ? "Cambiar foto" : "Subir foto"}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  hidden
                  disabled={avatarBusy}
                  onChange={(event) => {
                    const file = event.target.files?.[0] ?? null;
                    event.currentTarget.value = "";
                    if (file) onUploadAvatar?.(file);
                  }}
                />
              </label>
              {profile.avatar_url && (
                <button type="button" className="btn btn--ghost btn--compact btn--tap44" disabled={avatarBusy} onClick={() => onRemoveAvatar?.()}>
                  Quitar
                </button>
              )}
            </div>
          )}
        </div>
        <div>
          <span className="player-pro-eyebrow">Perfil deportivo</span>
          <h1>{isPublicProfile ? profile.display_name : `Hola, ${profile.first_name}`}</h1>
          <p>{subtitle}</p>
        </div>
      </div>
      <div className="player-athlete-hero__stats" aria-label="Resumen competitivo">
        <div>
          <span>Récord</span>
          <strong>{recordLabel(data.stats)}</strong>
          <em>{formatPercent(data.stats.win_rate)} efectividad</em>
        </div>
        <div>
          <span>Torneos</span>
          <strong>{data.summary.tournaments_active}</strong>
          <em>{data.summary.tournaments_total} total</em>
        </div>
        <div>
          <span>Ranking</span>
          {/* Rank del torneo activo (Eduardo 2026-09-24): el seed que puso el
              organizador (Paso Ranking) y el lugar actual conforme se juega. */}
          <strong>{currentRank ? `#${currentRank}` : initialRank ? `#${initialRank}` : "—"}</strong>
          <em>
            {initialRank && currentRank
              ? `#${initialRank} → #${currentRank} en tu torneo`
              : initialRank
                ? "Seed del organizador en tu torneo"
                : "Sin rank en tu torneo aún"}
          </em>
        </div>
        <div>
          <span>Rank FMT</span>
          <strong>{fmtRanking ? `#${fmtRanking.position}` : "—"}</strong>
          <em>{fmtRanking ? `${fmtRanking.category_name} · ${fmtRanking.points_total} pts` : "Sin ranking oficial"}</em>
        </div>
        <div>
          <span>WTN</span>
          <strong>{profile.wtn_rating != null ? profile.wtn_rating.toFixed(1) : "—"}</strong>
          <em>
            {profile.wtn_rating_doubles != null
              ? `Dobles ${profile.wtn_rating_doubles.toFixed(1)}`
              : profile.wtn_tennis_id
                ? `ID ${profile.wtn_tennis_id}`
                : "Sin WTN aún"}
          </em>
        </div>
      </div>
      {featured && (
        <div className="player-athlete-hero__progress">
          <span>Progreso destacado</span>
          <strong>{featured.tournament?.name ?? "Torneo activo"}</strong>
          <RankMovementBadge
            movement={featured.participant.rank_movement}
            delta={featured.participant.rank_delta}
          />
        </div>
      )}
      {!isPublicProfile && (
        <dl className="player-athlete-hero__contact">
          <div>
            <dt>Email</dt>
            <dd>{profile.email ?? "—"}</dd>
          </div>
          <div>
            <dt>Usuario</dt>
            <dd>{profile.username ?? profile.email ?? "—"}</dd>
          </div>
          <div>
            <dt>Teléfono</dt>
            <dd>{profile.phone ?? "—"}</dd>
          </div>
        </dl>
      )}
      <div className="player-athlete-hero__actions">
        <Link className="btn btn--ghost" to={routes.player}>Home</Link>
        <Link className="btn btn--ghost" to="/player/tournaments">Torneos</Link>
      </div>
    </section>
  );
}

function AchievementsShowcase({ achievements }: { achievements: PlayerProfileAchievement[] }) {
  return (
    <section className="card player-achievements-card">
      <div className="card__header-row">
        <div>
          <h2 className="card__title">🏆 Vitrina de logros</h2>
          <p className="card__subtitle">Reconocimientos derivados de tu actividad y resultados.</p>
        </div>
      </div>
      {achievements.length === 0 ? (
        <div className="player-achievements-empty">
          <strong>Tus trofeos aparecerán aquí.</strong>
          <span>Completa torneos, sube ranking o captura victorias para desbloquear logros.</span>
        </div>
      ) : (
        <div className="player-achievements-grid">
          {achievements.map((achievement) => (
            <article key={achievement.code} className={`player-achievement player-achievement--${achievement.tone}`}>
              <span className="player-achievement__icon">{achievement.icon}</span>
              <strong>{achievement.title}</strong>
              <p>{achievement.description}</p>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

function ActiveTournamentTable({ rows }: { rows: PlayerProfileTournamentRow[] }) {
  if (rows.length === 0) {
    return <p className="muted">Sin torneos activos.</p>;
  }
  return (
    <div className="responsive-table-wrap">
      <table className="data-table dashboard-table">
        <thead>
          <tr>
            <th>Torneo</th>
            <th>Estado</th>
            <th>Categoría</th>
            <th>Grupo</th>
            <th className="data-table__cell--num">Rank inicial</th>
            <th className="data-table__cell--num">Rank actual</th>
            <th>Cambio</th>
            <th className="data-table__cell--num">Posición</th>
            <th className="data-table__cell--num">PJ</th>
            <th className="data-table__cell--num">G/P</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const t = row.tournament;
            return (
              <tr key={row.participant.id}>
                <td className="data-table__cell--name">{t?.name ?? "—"}</td>
                <td>{participantStatusLabel(row.participant.current_status)}</td>
                <td>{row.current_category?.name ?? "—"}</td>
                <td>{row.stage_locked ? <span className="muted" data-no-toast>En armado</span> : row.group?.name ?? "—"}</td>
                <td className="data-table__cell--num">{row.participant.initial_group_rank ?? row.participant.initial_rank ?? row.participant.tournament_rank ?? "—"}</td>
                <td className="data-table__cell--num">{row.participant.group_position ?? row.participant.current_rank ?? "—"}</td>
                <td>
                  <RankMovementBadge movement={row.participant.rank_movement} delta={row.participant.rank_delta} />
                </td>
                <td className="data-table__cell--num">{row.standings_position ?? "—"}</td>
                <td className="data-table__cell--num">{row.stats.matches_played}</td>
                <td className="data-table__cell--num">{row.stats.wins}/{row.stats.losses}</td>
                <td>
                  {t ? (
                    <div className="table-actions">
                      <Link className="btn btn--ghost btn--compact" to={`/player/tournaments/${t.id}/standings`}>Ranking</Link>
                      <Link className="btn btn--ghost btn--compact" to={`/player/tournaments/${t.id}/rules`}>Reglamento</Link>
                    </div>
                  ) : "—"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function HistoryTable({ rows }: { rows: PlayerProfileTournamentRow[] }) {
  if (rows.length === 0) {
    return (
      <div className="player-profile-empty-state">
        <strong>Tu historial competitivo se irá formando aquí.</strong>
        <span>Aquí aparecerán tus torneos terminados, mejores posiciones y resultados finales.</span>
      </div>
    );
  }
  return (
    <div className="responsive-table-wrap">
      <table className="data-table dashboard-table">
        <thead>
          <tr>
            <th>Torneo</th>
            <th>Tipo</th>
            <th>Categoría</th>
            <th>Resultado final</th>
            <th className="data-table__cell--num">Rank inicial</th>
            <th className="data-table__cell--num">Rank final</th>
            <th>Cambio</th>
            <th className="data-table__cell--num">PJ</th>
            <th className="data-table__cell--num">G</th>
            <th className="data-table__cell--num">P</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const t = row.tournament;
            return (
              <tr key={row.participant.id}>
                <td className="data-table__cell--name">{t?.name ?? "—"}</td>
                <td>{tournamentTypeLabel(t?.tournament_type)}</td>
                <td>{row.current_category?.name ?? "—"}</td>
                <td>{row.final_result?.label ?? "—"}</td>
                <td className="data-table__cell--num">{row.participant.initial_group_rank ?? row.participant.initial_rank ?? row.participant.tournament_rank ?? "—"}</td>
                <td className="data-table__cell--num">{row.participant.group_position ?? row.participant.current_rank ?? "—"}</td>
                <td><RankMovementBadge movement={row.participant.rank_movement} delta={row.participant.rank_delta} /></td>
                <td className="data-table__cell--num">{row.stats.matches_played}</td>
                <td className="data-table__cell--num">{row.stats.wins}</td>
                <td className="data-table__cell--num">{row.stats.losses}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Eduardo 2026-10-06: el marcador se muestra desde quien ganó (aunque haya
 * perdido el dueño del perfil); se guarda desde el jugador 1. */
function profileMatchScoreFromWinner(m: PlayerProfileMatch): string {
  return matchScoreFromWinner({
    score_text: m.score_text,
    winner_participant_id: m.winner_participant_id,
    player1_participant_id: m.player1?.participant_id ?? null,
    player2_participant_id: m.player2?.participant_id ?? null,
  }).trim();
}

function ResultsCards({ matches }: { matches: PlayerProfileMatch[] }) {
  if (matches.length === 0) {
    return <p className="muted">Aún no tienes resultados.</p>;
  }
  return (
    <div className="player-results-grid">
      {matches.map((m) => {
        const label = m.result === "win" ? "Ganado" : m.result === "loss" ? "Perdido" : m.result === "draw" ? "Empate" : m.result === "pending" ? "Pendiente" : m.result === "double_walkover" ? "Doble W.O." : "—";
        return (
          <article key={m.id} className={`player-result-card player-result-card--${m.result}`}>
            <div>
              <span className="player-result-card__badge">{label}</span>
              <strong>{m.opponent?.display_name ?? "Rival por definir"}</strong>
              <p>{m.tournament_name ?? "Torneo"} · {m.round_display_label ?? m.round_label ?? "Partido"}</p>
            </div>
            <div className="player-result-card__score">{profileMatchScoreFromWinner(m) || "—"}</div>
            <span className="player-result-card__date">
              {formatDate(m.scheduled_date)}{m.scheduled_time ? ` ${m.scheduled_time}` : ""}
            </span>
          </article>
        );
      })}
    </div>
  );
}

export default function PlayerProfilePage() {
  const { token } = useAuth();
  const params = useParams<{ playerProfileId?: string }>();
  const viewedPlayerId = params.playerProfileId ? Number(params.playerProfileId) : null;
  const isPublicProfile = Boolean(viewedPlayerId);
  const [data, setData] = useState<PlayerProfilePayload | null>(null);
  const scope = usePlayerProfileScope();
  const familyProfiles = scope.profiles;
  const activeProfileId = scope.profileId;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Cambiar de hijo rápido (o desde otra pestaña): una respuesta tardía del
  // hijo anterior no pisa a la del nuevo.
  const latestLoad = useMemo(() => makeLatestOnly(), []);

  const load = useCallback(async () => {
    if (!token) return;
    if (!isPublicProfile && !scope.ready) return;
    const attempt = latestLoad.begin();
    setLoading(true);
    setError(null);
    try {
      const response = viewedPlayerId && Number.isFinite(viewedPlayerId)
        ? await api.player.globalProfile(token, viewedPlayerId)
        : await api.player.profile(token, activeProfileId);
      if (!latestLoad.isCurrent(attempt)) return;
      setData(response.data);
    } catch (err) {
      if (!latestLoad.isCurrent(attempt)) return;
      if (err instanceof ApiError) {
        if (err.status === 404) {
          setError("Tu cuenta no está vinculada a un perfil de jugador. Pídele al admin que te genere un PIN.");
        } else {
          setError(err.message || "No pudimos cargar tu perfil.");
        }
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      if (latestLoad.isCurrent(attempt)) setLoading(false);
    }
  }, [activeProfileId, isPublicProfile, latestLoad, scope.ready, token, viewedPlayerId]);

  useEffect(() => {
    void load();
  }, [load]);

  const achievements = useMemo(() => data?.achievements ?? [], [data]);
  const selectedProfileId = activeProfileId ?? data?.profile.id ?? null;

  // Foto de perfil (Eduardo 2026-09-08): subir/quitar desde Mi perfil.
  // Cuenta familiar: la foto es del hijo que SE VE (no del recién elegido
  // mientras su perfil llega) y el resultado solo se pinta si sigue en
  // pantalla. Mientras cambia de hijo no hay botones de foto.
  const shownProfileId = data?.profile.id ?? null;
  const showingChosenProfile = isPublicProfile || activeProfileId == null || shownProfileId === activeProfileId;
  const shownProfileRef = useRef<number | null>(shownProfileId);
  // Se actualiza al pintar (no en un efecto): un clic justo después de que
  // cambia el hijo en pantalla ya debe ir al hijo que se ve.
  shownProfileRef.current = shownProfileId;
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const handleUploadAvatar = useCallback(async (file: File) => {
    const targetProfileId = shownProfileRef.current;
    if (!token || targetProfileId === null) return;
    setAvatarBusy(true);
    setAvatarError(null);
    try {
      const response = await api.player.uploadAvatar(token, file, targetProfileId);
      setData((prev) =>
        prev && prev.profile.id === targetProfileId
          ? { ...prev, profile: { ...prev.profile, avatar_url: response.data.profile.avatar_url } }
          : prev,
      );
    } catch (err) {
      if (shownProfileRef.current !== targetProfileId) return;
      setAvatarError(err instanceof ApiError ? err.message || "No pudimos subir la foto." : "Error de red. Intenta otra vez.");
    } finally {
      setAvatarBusy(false);
    }
  }, [token]);
  const handleRemoveAvatar = useCallback(async () => {
    const targetProfileId = shownProfileRef.current;
    if (!token || targetProfileId === null) return;
    setAvatarBusy(true);
    setAvatarError(null);
    try {
      await api.player.deleteAvatar(token, targetProfileId);
      setData((prev) =>
        prev && prev.profile.id === targetProfileId ? { ...prev, profile: { ...prev.profile, avatar_url: null } } : prev,
      );
    } catch (err) {
      if (shownProfileRef.current !== targetProfileId) return;
      setAvatarError(err instanceof ApiError ? err.message || "No pudimos quitar la foto." : "Error de red. Intenta otra vez.");
    } finally {
      setAvatarBusy(false);
    }
  }, [token]);
  const handleProfileChange = (profileId: number) => {
    scope.setProfileId(profileId);
  };
  const finalizedRecentMatches = useMemo(
    () => (data?.recent_matches ?? []).filter((match) => match.result !== "pending"),
    [data],
  );

  if (loading && !data) {
    return <section className="card"><p className="muted">Cargando…</p></section>;
  }

  if (error) {
    return <section className="card"><p className="form-error" role="alert">{error}</p></section>;
  }

  if (!data) return null;

  return (
    <>
      {!isPublicProfile && (
        <section className="card player-family-profile-card">
          <div>
            <span className="player-pro-eyebrow">Cuenta familiar</span>
            <h2 className="card__title">Estás viendo: {data.profile.display_name}</h2>
          </div>
          <PlayerFamilyProfileSwitcher
            familyProfiles={familyProfiles}
            activeProfileId={selectedProfileId}
            onChange={handleProfileChange}
            disabled={avatarBusy}
          />
        </section>
      )}

      <AthleteHero
        data={data}
        isPublicProfile={isPublicProfile}
        avatarBusy={avatarBusy}
        onUploadAvatar={isPublicProfile || !showingChosenProfile ? undefined : handleUploadAvatar}
        onRemoveAvatar={isPublicProfile || !showingChosenProfile ? undefined : handleRemoveAvatar}
      />
      {avatarError && (
        <p role="alert" className="form-error player-athlete-hero__avatar-error">
          {avatarError}
        </p>
      )}

      <section className="card">
        <h2 className="card__title">Resumen</h2>
        <div className="metric-grid">
          <StatTile label="Torneos" value={data.summary.tournaments_total} hint={`${data.summary.tournaments_active} activos`} accent />
          <StatTile label="Partidos" value={data.stats.matches_played} />
          <StatTile label="Ganados" value={data.stats.wins} hint={formatPercent(data.stats.win_rate)} />
          <StatTile label="Perdidos" value={data.stats.losses} />
          <StatTile label="Sets" value={`${data.stats.sets_for}-${data.stats.sets_against}`} hint="ganados-perdidos" />
          <StatTile label="Games" value={`${data.stats.games_for}-${data.stats.games_against}`} hint={`Diferencia ${data.stats.differential >= 0 ? `+${data.stats.differential}` : data.stats.differential}`} />
        </div>
      </section>

      <AchievementsShowcase achievements={achievements} />

      <section className="card">
        <h2 className="card__title">Torneos activos</h2>
        <ActiveTournamentTable rows={data.active_tournaments} />
      </section>

      <section className="card">
        <div className="card__header-row">
          <div>
            <h2 className="card__title">Draw reciente</h2>
          </div>
          {!isPublicProfile && (
            <Link className="btn btn--ghost btn--compact" to="/player/profile/results">
              Ver Draw histórico
            </Link>
          )}
        </div>
        <ResultsCards matches={finalizedRecentMatches} />
      </section>

      <section className="card">
        <h2 className="card__title">Historial de torneos</h2>
        <HistoryTable rows={data.tournament_history} />
      </section>
    </>
  );
}
